import { useCallback, useEffect, useRef, useState } from 'react';
import * as api from '../api';

export default function usePublicChannels({ activeIdentity, user, triggerAlert, enabled = false }) {
  const [publicChannels, setPublicChannels] = useState([]);
  const [activePublicChannel, setActivePublicChannel] = useState(null);
  const [publicChannelPosts, setPublicChannelPosts] = useState([]);
  const [publicChannelsError, setPublicChannelsError] = useState('');
  const [publicChannelsLoading, setPublicChannelsLoading] = useState(false);
  const [publicChannelPostsLoading, setPublicChannelPostsLoading] = useState(false);
  const notificationPrimedRef = useRef(false);

  const refreshPublicChannels = useCallback(async () => {
    if (!user || !enabled) return;
    setPublicChannelsError('');
    setPublicChannelsLoading(true);
    try {
      const result = await api.getPublicChannels();
      const channels = Array.isArray(result?.channels) ? result.channels : [];
      setPublicChannels(channels);
      setActivePublicChannel(prev => {
        if (!prev) return prev;
        return channels.find(channel => channel.id === prev.id) || prev;
      });
    } catch (error) {
      setPublicChannelsError(error.message || 'Could not load channels.');
    } finally {
      setPublicChannelsLoading(false);
    }
  }, [enabled, user]);

  const refreshPublicChannelPosts = useCallback(async channelId => {
    if (!channelId) {
      setPublicChannelPosts([]);
      return;
    }
    setPublicChannelPostsLoading(true);
    try {
      const result = await api.getPublicChannelPosts(channelId, 80);
      const posts = Array.isArray(result?.posts) ? result.posts : [];
      setPublicChannelPosts(posts.slice().reverse());
    } catch (error) {
      triggerAlert('Posts unavailable', error.message || 'Could not load channel posts.', 'danger');
    } finally {
      setPublicChannelPostsLoading(false);
    }
  }, [triggerAlert]);

  useEffect(() => {
    if (!enabled) return;
    refreshPublicChannels();
  }, [enabled, refreshPublicChannels]);

  useEffect(() => {
    refreshPublicChannelPosts(activePublicChannel?.id);
  }, [activePublicChannel?.id, refreshPublicChannelPosts]);

  useEffect(() => {
    if (!enabled || !user || !activeIdentity || publicChannels.length === 0) return undefined;
    const storageKey = `gaiacom_public_channel_seen_${user.id}`;
    const readSeen = () => {
      try {
        return JSON.parse(localStorage.getItem(storageKey) || '{}');
      } catch (_) {
        return {};
      }
    };
    const writeSeen = value => {
      localStorage.setItem(storageKey, JSON.stringify(value));
    };
    const pollSubscribedChannels = async () => {
      const subscribed = publicChannels.filter(channel => channel.isSubscribed);
      if (subscribed.length === 0) return;
      const seen = readSeen();
      let changed = false;
      for (const channel of subscribed) {
        try {
          const result = await api.getPublicChannelPosts(channel.id, 5);
          const posts = Array.isArray(result?.posts) ? result.posts : [];
          const newest = posts[0];
          if (!newest?.id) continue;
          const previous = seen[channel.id];
          if (previous && previous !== newest.id && notificationPrimedRef.current) {
            if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
              new Notification(channel.name || 'GaiaCom Channel', {
                body: newest.body ? newest.body.slice(0, 90) : 'New encrypted channel update'
              });
            }
          }
          if (previous !== newest.id) {
            seen[channel.id] = newest.id;
            changed = true;
          }
        } catch (_) {}
      }
      if (changed) {
        writeSeen(seen);
      }
      notificationPrimedRef.current = true;
    };
    pollSubscribedChannels();
    const interval = window.setInterval(pollSubscribedChannels, 10000);
    return () => window.clearInterval(interval);
  }, [activeIdentity, enabled, publicChannels, user]);

  const createChannel = useCallback(async ({ name, description, avatar }) => {
    if (!activeIdentity?.ID) throw new Error('Active identity required.');
    const channel = await api.createPublicChannel(activeIdentity.ID, name, description, avatar || null);
    await refreshPublicChannels();
    setActivePublicChannel(channel);
    triggerAlert('Channel created', `"${channel.name}" is now public.`);
  }, [activeIdentity, refreshPublicChannels, triggerAlert]);

  const updateChannel = useCallback(async ({ channelId, name, description, avatar }) => {
    const channel = await api.updatePublicChannel(channelId, name, description, avatar || null);
    await refreshPublicChannels();
    setActivePublicChannel(channel);
    triggerAlert('Channel updated', 'Channel metadata was saved.');
  }, [refreshPublicChannels, triggerAlert]);

  const toggleSubscription = useCallback(async channel => {
    if (!activeIdentity?.ID || !channel?.id) return;
    const updated = channel.isSubscribed
      ? await api.unsubscribePublicChannel(activeIdentity.ID, channel.id)
      : await api.subscribePublicChannel(activeIdentity.ID, channel.id);
    setPublicChannels(prev => prev.map(item => item.id === updated.id ? updated : item));
    setActivePublicChannel(prev => prev?.id === updated.id ? updated : prev);
    triggerAlert(
      updated.isSubscribed ? 'Subscribed' : 'Unsubscribed',
      updated.isSubscribed ? `You subscribed to "${updated.name}".` : `You unsubscribed from "${updated.name}".`
    );
  }, [activeIdentity, triggerAlert]);

  const createPost = useCallback(async ({ body, attachments }) => {
    if (!activeIdentity?.ID || !activePublicChannel?.id) throw new Error('Channel and identity required.');
    const post = await api.createPublicChannelPost(
      activeIdentity.ID,
      activePublicChannel.id,
      body,
      { mode: 'markdown-lite' },
      attachments || null
    );
    setPublicChannelPosts(prev => [...prev, post]);
    await refreshPublicChannels();
    triggerAlert('Published', 'Channel post published.');
  }, [activeIdentity, activePublicChannel, refreshPublicChannels, triggerAlert]);

  const reportChannel = useCallback(async ({ channelId, category, severity, comment }) => {
    if (!activeIdentity?.ID) throw new Error('Active identity required.');
    await api.submitAbuseReport(activeIdentity.ID, 'channel', channelId, category, severity, null, comment);
    triggerAlert('Meldung eingereicht', 'Kanalmeldung wurde im Abuse-Consensus registriert.');
  }, [activeIdentity, triggerAlert]);

  const deleteChannel = useCallback(async (channelId) => {
    await api.deletePublicChannel(channelId);
    await refreshPublicChannels();
    setActivePublicChannel(null);
    triggerAlert('Kanal gelöscht', 'Der öffentliche Kanal wurde erfolgreich gelöscht.');
  }, [refreshPublicChannels, triggerAlert]);

  const verifyChannel = useCallback(async (channelId, verified) => {
    if (!activeIdentity?.ID) throw new Error('Active identity required.');
    await api.applyNodeOperatorAction(
      activeIdentity.ID,
      'verify_channel',
      channelId,
      verified,
      'Zertifizierung'
    );
    setPublicChannels(prev => prev.map(item => {
      if (item.id === channelId) {
        return { ...item, isVerified: verified };
      }
      return item;
    }));
    setActivePublicChannel(prev => {
      if (prev && prev.id === channelId) {
        return { ...prev, isVerified: verified };
      }
      return prev;
    });
    triggerAlert('Erfolg', verified ? 'Kanal wurde zertifiziert.' : 'Zertifizierung wurde entzogen.');
  }, [activeIdentity, triggerAlert]);

  return {
    publicChannels,
    setPublicChannels,
    activePublicChannel,
    setActivePublicChannel,
    publicChannelPosts,
    publicChannelsError,
    publicChannelsLoading,
    publicChannelPostsLoading,
    refreshPublicChannels,
    refreshPublicChannelPosts,
    createChannel,
    updateChannel,
    toggleSubscription,
    createPost,
    reportChannel,
    deleteChannel,
    verifyChannel
  };
}
